import { SECURITY_TYPE_ALIAS, SHARES_FULLY_DILUTED_ALIAS, SHARES_OUTSTANDING_ALIAS, UNDERLYING_SECURITY_ALIAS } from 'common/constants/cap-table/capTable';
import { UNISSUED_OPTIONS } from 'common/constants/securityTypes';

// ONE source of truth for the AI field-name translation, shared by the parser
// (attaching ai_finding metadata to grid cells) and securityAiPatches (store
// accept/dismiss patches). Kept as a LEAF module - parser.ts drags the heavy
// `utilities` barrel (ESM deps jest cannot transform), which would poison every
// light consumer/test of the patches.
//
// ai_finding.field_metadata is keyed by Security MODEL field names (the backend's
// AI_FIELD_TO_SECURITY_FIELD in cap_table_generation_job_service.py), while grid
// rows use CELL aliases. For most fields the two coincide; the exceptions below
// must be resolved or their findings never attach to a cell (no AI rendering, no
// metadata-backed review card) and store patches silently miss. Review flows keep
// SENDING cell aliases unchanged - the backend accepts both synonyms.
export const AI_METADATA_KEY_BY_ALIAS: Record<string, string> = {
  [SECURITY_TYPE_ALIAS]: 'security_type_id',
  [UNDERLYING_SECURITY_ALIAS]: 'underlying_security',
};

// Inverse view: metadata key -> the Security field the grid actually READS (the
// parser reads column[row.alias]). Writing an accepted value onto the metadata
// key itself (e.g. `underlying_security`) is invisible to the grid.
export const SECURITY_FIELD_BY_AI_METADATA_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(AI_METADATA_KEY_BY_ALIAS).map(([alias, key]) => [key, alias])
);

// Canonical metadata key for a field name arriving as EITHER a cell alias or a
// metadata key (identity for every unmapped field).
export const aiMetadataKeyOf = (fieldName: string): string => AI_METADATA_KEY_BY_ALIAS[fieldName] ?? fieldName;

// Citation/mapping references have the same key-vs-alias split, plus a third
// spelling: rows written straight from the AI results carry the RAW camelCase
// data-point name (e.g. field_name='underlyingSecurity' observed live), which
// matches NO row alias on any FE. Accept every known synonym when keying a
// column's references to a row; everything the FE SENDS stays the cell alias.
export const REFERENCE_FIELD_SYNONYMS_BY_ALIAS: Record<string, string[]> = {
  [SECURITY_TYPE_ALIAS]: ['security_type_id', 'securityType'],
  [UNDERLYING_SECURITY_ALIAS]: ['underlying_security', 'underlyingSecurity'],
};

// Every spelling a reference's field_name may use for the given field names
// (alias, model key, raw camelCase) - so a decline keyed by EITHER spelling
// still hits refs stored under any of them.
export const referenceFieldNameSet = (fieldNames: string[]): Set<string> => {
  const names = new Set(fieldNames);
  fieldNames.forEach(fieldName => {
    const key = aiMetadataKeyOf(fieldName);
    const alias = SECURITY_FIELD_BY_AI_METADATA_KEY[key] ?? key;
    names.add(key);
    names.add(alias);
    (REFERENCE_FIELD_SYNONYMS_BY_ALIAS[alias] ?? []).forEach(synonym => names.add(synonym));
  });
  return names;
};

type AiFindingColumn = { ai_finding?: { field_metadata?: Record<string, unknown> | null } | null };

/** Deletes a grid column's AI suggestions for these field names, so a rebuild cannot revive them; true when any went. */
export const dropAiFieldFromColumn = (column: AiFindingColumn | null | undefined, fieldNames: Set<string>): boolean => {
  const metadata = column?.ai_finding?.field_metadata ?? {};
  const dropped = Object.keys(metadata).filter(key => fieldNames.has(key));
  dropped.forEach(key => delete metadata[key]);
  return dropped.length > 0;
};


type AiSecurity = AiFindingColumn & { security_type?: unknown };

// A pool's count suggestion sits under shares_outstanding while it shows on the diluted cell.
const isPoolCountCard = (security: AiSecurity | undefined, alias: string, pooled: boolean): boolean => {
  const metadata = security?.ai_finding?.field_metadata as Record<string, { value?: unknown }> | undefined;
  return pooled && alias === SHARES_FULLY_DILUTED_ALIAS && metadata?.[SHARES_FULLY_DILUTED_ALIAS]?.value == null && metadata?.[SHARES_OUTSTANDING_ALIAS] != null;
};

const acceptedAsPool = (security: AiSecurity | undefined): boolean => {
  const outstanding = security?.ai_finding?.field_metadata?.[SHARES_OUTSTANDING_ALIAS];
  return outstanding != null && typeof outstanding === 'object' && 'applied_previous_value' in outstanding;
};

/** The metadata key a grid cell shows: a pool's diluted cell shows its count suggestion. */
export const aiMetadataKeyForSecurity = (security: AiSecurity | undefined, alias: string): string =>
  isPoolCountCard(security, alias, security?.security_type === UNISSUED_OPTIONS) ? SHARES_OUTSTANDING_ALIAS : aiMetadataKeyOf(alias);

/** The metadata key a review decision settles: a count accepted as a pool keeps its diluted card after a type revert. */
export const aiReviewKeyForSecurity = (security: AiSecurity | undefined, alias: string): string =>
  isPoolCountCard(security, alias, security?.security_type === UNISSUED_OPTIONS || acceptedAsPool(security))
    ? SHARES_OUTSTANDING_ALIAS
    : aiMetadataKeyOf(alias);

export const aiFieldAliasForSecurity = (security: AiSecurity | undefined, field: string): string =>
  security?.security_type === UNISSUED_OPTIONS && field === SHARES_OUTSTANDING_ALIAS
    ? SHARES_FULLY_DILUTED_ALIAS
    : (SECURITY_FIELD_BY_AI_METADATA_KEY[aiMetadataKeyOf(field)] ?? field);

export const referenceFieldsForSecurity = (security: AiSecurity | undefined, field: string): string[] => {
  const alias = aiFieldAliasForSecurity(security, field);
  const fields = [field, aiMetadataKeyForSecurity(security, alias), alias];
  if (security?.security_type === UNISSUED_OPTIONS && alias === SHARES_FULLY_DILUTED_ALIAS) {
    fields.push(SHARES_OUTSTANDING_ALIAS, 'numberOfShares', 'asConvertedShares');
  }
  return fields;
};


/** Keeps API aliases stable while a pool count card sends the key its suggestion is saved under. */
export const aiRequestFieldForSecurity = (security: AiSecurity | undefined, field: string): string =>
  field === SHARES_FULLY_DILUTED_ALIAS ? aiReviewKeyForSecurity(security, field) : field;

/** Clearing the visible pool count clears both share-count scopes, since the grid shows them as one cell. */
export const aiClearFieldsForSecurity = (security: AiSecurity | undefined, field: string): string[] =>
  security?.security_type === UNISSUED_OPTIONS && field === SHARES_FULLY_DILUTED_ALIAS
    ? [SHARES_FULLY_DILUTED_ALIAS, SHARES_OUTSTANDING_ALIAS]
    : [field];
